import React, { useEffect, useState } from 'react';
import { UserCog, Loader2, Star, Bike, Zap } from 'lucide-react';
import { adminDriverApi } from '../services/api';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

/**
 * AdminReassignDriverButton — admin-side per-order driver reassignment.
 * Unlike the vendor dialog, admins can pick ANY approved driver (no zone/city
 * filter). Also offers a "Broadcast to Super Drivers" broadcast and an
 * "Auto-assign nearest" fallback that runs the same logic the platform uses on
 * new orders.
 */
const AdminReassignDriverButton = ({ orderId, currentDriverId, currentDriverName, onReassigned }) => {
  const [open, setOpen] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    adminDriverApi.list('approved')
      .then((rows) => setDrivers(Array.isArray(rows) ? rows : []))
      .catch(() => toast.error('Could not load drivers'))
      .finally(() => setLoading(false));
  }, [open]);

  const doReassign = async (payload, label) => {
    setBusy(true);
    try {
      const r = await adminDriverApi.reassignOrder(orderId, payload);
      toast.success(label || `Reassigned (${r.action || 'ok'})`);
      setOpen(false);
      onReassigned?.(r);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reassign failed'); }
    finally { setBusy(false); }
  };

  const filtered = drivers.filter((d) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (d.name || '').toLowerCase().includes(q)
      || (d.driver_id || '').toLowerCase().includes(q)
      || (d.home_city || '').toLowerCase().includes(q);
  });

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="text-slate-700" data-testid={`admin-reassign-btn-${orderId}`}>
        <UserCog className="h-3.5 w-3.5 mr-1" />Reassign
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5 text-emerald-600" />Reassign order {String(orderId).slice(-6)}
            </DialogTitle>
            <DialogDescription>
              {currentDriverId ? <>Currently assigned to <span className="font-medium">{currentDriverName || currentDriverId}</span>.</> : 'No driver assigned yet.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                onClick={() => doReassign({ prefer_super: true }, 'Broadcast sent to Super Drivers')}
                disabled={busy}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid={`admin-reassign-super-${orderId}`}
              >
                <Star className="h-4 w-4 mr-1 fill-white" />Super broadcast
              </Button>
              <Button
                type="button"
                onClick={() => doReassign({}, 'Auto-assigned to nearest driver')}
                disabled={busy}
                variant="outline"
                data-testid={`admin-reassign-auto-${orderId}`}
              >
                <Zap className="h-4 w-4 mr-1" />Auto-nearest
              </Button>
            </div>
            <div className="text-[11px] text-slate-500 text-center">— or pick any approved driver —</div>
            <input
              type="text"
              placeholder="Search by name, city, id"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid={`admin-reassign-search-${orderId}`}
            />
            {loading ? (
              <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-4">No approved drivers match.</div>
            ) : (
              <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid={`admin-reassign-list-${orderId}`}>
                {filtered.map((d) => (
                  <button
                    key={d.driver_id}
                    type="button"
                    onClick={() => doReassign({ driver_id: d.driver_id }, `Sent to ${d.name}. They'll see it in Available Deliveries.`)}
                    disabled={busy || d.driver_id === currentDriverId}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-slate-50 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`admin-reassign-pick-${orderId}-${d.driver_id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Bike className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-sm text-slate-800 truncate">{d.name}</span>
                      {d.is_super_driver && <Badge className="bg-indigo-100 text-indigo-800 border-0 hover:bg-indigo-100 text-[10px]">★ Super</Badge>}
                      <span className="text-[11px] text-slate-400">· {d.home_city || d.vehicle_type || 'Driver'}</span>
                    </div>
                    <span className={`text-[11px] font-medium ${d.is_online ? 'text-emerald-700' : 'text-slate-400'}`}>{d.is_online ? 'Online' : 'Offline'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminReassignDriverButton;
